import ExcelJS from "exceljs"
import {
  addTablesToWorkbook,
  TableExport,
} from "utils/excel/exportTableModels"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import {
  effectiveTableStyle,
  TableStyleChoice,
} from "types/tableStyle.types"
import {
  DASHBOARD_TABLE_STYLE,
  DataTile,
} from "features/Dashboard/types/dashboardDocument"
import { ExportEntry } from "./collectExportTiles"

export const COVER_SHEET_NAME = "Cover"

const TYPE_LABEL: Record<DataTile["type"], string> = {
  kpi: "Number",
  bar: "Bar chart",
  line: "Line chart",
  pie: "Pie chart",
  table: "Table",
  scorecard: "Scorecard",
}

/** Muted grey of the cover's date line. */
const COVER_MUTED = "FF6B7280"

/**
 * Longest sheet name before a repeat's suffix: Excel allows 31 characters,
 * which leaves room for " (99)".
 */
const SHEET_BASE = 26

/** Names a tile's sheet never takes: the cover's, and one Excel keeps. */
const RESERVED_SHEET_NAMES = [COVER_SHEET_NAME, "History"]

/** `name` without apostrophes at either end, which Excel refuses there. */
const trimApostrophes = (name: string): string =>
  name.replace(/^[\s']+|[\s']+$/g, "")

/**
 * Sheet names for `names`, in order: characters Excel refuses become
 * spaces, apostrophes at either end go, each name is cut to 26 characters
 * (room for " (99)"), and a repeat, ignoring case, gets " (2)", " (3)". The
 * reserved names (the Cover sheet, and "History", which Excel keeps) count
 * as taken, so a tile named so gets " (2)".
 */
export const uniqueSheetNames = (names: string[]): string[] => {
  const seen = new Map<string, number>(
    RESERVED_SHEET_NAMES.map((n) => [n.toLowerCase(), 1])
  )
  return names.map((raw) => {
    const cleaned = trimApostrophes(raw.replace(/[\\/?*[\]:]/g, " "))
    const base = trimApostrophes(cleaned.slice(0, SHEET_BASE)) || "Sheet"
    const n = (seen.get(base.toLowerCase()) ?? 0) + 1
    seen.set(base.toLowerCase(), n)
    return n === 1 ? base : `${base} (${n})`
  })
}

/**
 * The sheet name of one panel of a repeated tile: "<Tile> - <Category>".
 * When that is too long for a sheet, the tile's name is cut first, so
 * sheets of the same tile still differ by their category.
 */
export const panelSheetName = (tileName: string, category: string): string => {
  const suffix = ` - ${category.trim()}`
  const full = `${tileName}${suffix}`
  if (full.length <= SHEET_BASE) return full
  const room = Math.max(4, SHEET_BASE - suffix.length)
  return `${tileName.slice(0, room).trimEnd()}${suffix}`
}

/** The first sheet of a filtered export: dashboard title, date and filters. */
const writeCover = (
  wb: ExcelJS.Workbook,
  title: string,
  summary: string
): void => {
  const ws = wb.addWorksheet(COVER_SHEET_NAME)
  ws.getColumn(1).width = 80
  const heading = ws.getCell(1, 1)
  heading.value = title
  heading.font = { bold: true, size: 14 }
  const date = ws.getCell(2, 1)
  date.value = `Exported ${new Date().toLocaleString()}`
  date.font = { color: { argb: COVER_MUTED } }
  const filters = ws.getCell(3, 1)
  filters.value = summary
  filters.alignment = { wrapText: true, vertical: "top" }
}

/**
 * The workbook for a dashboard export: one sheet per data tile (charts and
 * numbers export the table behind them), and one per panel of a repeated
 * tile, named "<Tile> - <Category>"; or every table on one sheet in
 * "combined" mode. An untitled tile is named by its type and its place
 * among the exported tiles. A table tile is written in its own table
 * style, every other tile in the dashboard's. When `summary` names active
 * filters, a Cover sheet comes first with the title, the date and the
 * summary.
 */
export const buildExcelWorkbook = (args: {
  entries: ExportEntry[]
  themeStyle: TableStyleChoice | undefined
  mode: "separate" | "combined"
  summary: string
  title: string
}): ExcelJS.Workbook => {
  const themeChoice = args.themeStyle ?? DASHBOARD_TABLE_STYLE
  const tileNames = new Map<string, string>()
  const nameOf = (tile: DataTile): string => {
    const known = tileNames.get(tile.id)
    if (known !== undefined) return known
    const name =
      tile.title?.trim() || `${TYPE_LABEL[tile.type]} ${tileNames.size + 1}`
    tileNames.set(tile.id, name)
    return name
  }
  const names = uniqueSheetNames(
    args.entries.map((e) =>
      e.panel
        ? panelSheetName(nameOf(e.tile), e.panel.category)
        : nameOf(e.tile)
    )
  )
  const tables: TableExport[] = args.entries.map((e, i) => ({
    name: names[i],
    model: e.model,
    style: resolveTableStyle(
      "report",
      e.tile.type === "table"
        ? effectiveTableStyle(e.tile.visual.tableStyle, themeChoice)
        : themeChoice
    ),
  }))
  const wb = new ExcelJS.Workbook()
  if (args.summary) writeCover(wb, args.title, args.summary)
  addTablesToWorkbook(wb, tables, {
    mode: args.mode,
    style: resolveTableStyle("report", themeChoice),
  })
  return wb
}
